/**
 * Main Layout Component
 * Provides the main layout structure with sidebar
 */

import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { RouteBreadcrumbs } from './RouteBreadcrumbs';
import { canManageAdminUsers } from '../../utils/permissions';

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Global route protection: Check if user tries to access admin-only Settings
  useEffect(() => {
    if (location.pathname.startsWith('/settings/admin-users') && !canManageAdminUsers()) {
      console.warn('Access denied: Redirecting user from Admin Users Settings');
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.default,
          overflow: 'auto',
          marginLeft: `${theme.layout.sidebar.width}px`, // Account for fixed sidebar width
        }}
      >
        {/* Page Content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            padding: theme.spacing(4),
          }}
        >
          <RouteBreadcrumbs />
          {children}
        </Box>
      </Box>
    </Box>
  );
};
